import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faExclamationTriangle,
  faCheckCircle,
  faSearch,
  faBell,
  faChartLine,
  faPlus,
  faEye,
  faClock,
  faSpinner,
  faProjectDiagram,
  faDatabase,
  faFileImport,
  faListCheck,
  faTachometerAlt,
  faCrosshairs
} from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import blueTeamService from '../services/blueTeamService';
import projectService from '../services/projectService';
import authService from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';

const BlueTeamDashboard = () => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const { projectId: urlProjectId } = useParams();
  const navigate = useNavigate();

  // Get current user to check role permissions
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const isPurpleTeam = currentUser?.role === 'purple_team' || currentUser?.team === 'purple';
  // Blue team users should not see red team attack details (that would be cheating!)
  const canSeeRedTeamData = isAdmin || isPurpleTeam;
  const [project, setProject] = useState(null);
  const [findings, setFindings] = useState([]);
  const [stats, setStats] = useState(null);
  const [detectionRate, setDetectionRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seedLoading, setSeedLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Use URL project ID, or fall back to localStorage
  const projectId = urlProjectId || localStorage.getItem('lastSelectedProject');

  useEffect(() => {
    if (projectId) {
      // If we have a project from localStorage but not in URL, redirect to proper URL
      if (!urlProjectId && projectId) {
        navigate(`/projects/${projectId}/blue-team`, { replace: true });
        return;
      }
      fetchProjectData();
    } else {
      // No project selected - show message to select one
      setLoading(false);
    }
  }, [projectId, urlProjectId, navigate]);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const [projectData, findingsData, statsData, detectionData] = await Promise.all([
        projectService.getProject(projectId),
        blueTeamService.getFindingsByProject(projectId),
        blueTeamService.getProjectStats(projectId),
        blueTeamService.getDetectionRate(projectId)
      ]);

      setProject(projectData);
      setFindings(Array.isArray(findingsData) ? findingsData : []);
      setStats(statsData);
      setDetectionRate(detectionData);
    } catch (error) {
      console.error('Error fetching project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedBlueTeam = async () => {
    setSeedLoading(true);
    try {
      const result = await blueTeamService.seedFindings(projectId);
      toast.success(`Seeded ${result.created} blue team findings (${result.detections} detections, ${result.falsePositives} false positives)`);
      fetchProjectData();
    } catch (error) {
      console.error('Error seeding blue team data:', error);
      toast.error(error.response?.data?.error || 'Failed to seed blue team data');
    } finally {
      setSeedLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-purple-100 text-purple-800';
      case 'investigating': return 'bg-blue-100 text-blue-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'false_positive': return 'bg-gray-100 text-gray-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'escalated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  // No project selected - prompt user to select from navigation
  if (!projectId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <FontAwesomeIcon icon={faProjectDiagram} className={`text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
        <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('blueTeam.noProjectSelected')}</h2>
        <p className={`mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          {t('blueTeam.selectProjectPrompt')}
        </p>
        <Link
          to="/projects"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('nav.projects')}
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', icon: faTachometerAlt, label: t('blueTeam.tabDashboard') || 'Dashboard' },
    { id: 'analysis', icon: faChartLine, label: t('blueTeam.tabAnalysis') || 'Analysis' },
    { id: 'findings', icon: faBell, label: t('blueTeam.tabFindings') || 'Findings', count: findings.length },
    ...(canSeeRedTeamData ? [{ id: 'detection', icon: faCrosshairs, label: t('blueTeam.tabDetection') || 'Detection' }] : [])
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('blueTeam.title')}
        subtitle={<>{t('blueTeam.subtitle')}{project && <span className="ml-2 text-blue-600">• {project.name}</span>}</>}
        icon={<FontAwesomeIcon icon={faShieldAlt} className="text-blue-600" />}
      >
        <Link
          to={`/projects/${projectId}/blue-team/import`}
          className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 inline-flex items-center"
        >
          <FontAwesomeIcon icon={faFileImport} className="mr-1.5 text-xs" />
          {t('siemImport.importButton') || 'Import SIEM Alerts'}
        </Link>
        <Link
          to={`/projects/${projectId}/blue-team/siem-review`}
          className="px-3 py-1.5 text-sm bg-teal-500 text-white rounded hover:bg-teal-600 inline-flex items-center"
        >
          <FontAwesomeIcon icon={faListCheck} className="mr-1.5 text-xs" />
          {t('siemReview.reviewButton') || 'SIEM Review'}
        </Link>
        <Link
          to={`/projects/${projectId}/blue-team/campaigns`}
          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 inline-flex items-center"
        >
          <FontAwesomeIcon icon={faProjectDiagram} className="mr-1.5 text-xs" />
          {t('blueTeam.planAssessment') || 'Plan Assessment'}
        </Link>
        <Link
          to={`/projects/${projectId}/blue-team/findings/new`}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 inline-flex items-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-1.5 text-xs" />
          {t('blueTeam.logDetection')}
        </Link>
        <Link
          to={`/projects/${projectId}/purple-team`}
          className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 inline-flex items-center"
        >
          <FontAwesomeIcon icon={faEye} className="mr-1.5 text-xs" />
          {t('blueTeam.purpleTeamView')}
        </Link>
      </PageHeader>

      {/* Tab Navigation */}
      <div className={`flex border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : darkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="mr-2" />
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ====== TAB: DASHBOARD ====== */}
      {activeTab === 'dashboard' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Findings */}
            <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('blueTeam.totalFindings')}</p>
                  <p className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats?.totalFindings || 0}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faBell} className="text-blue-600 text-xl" />
                </div>
              </div>
            </div>

            {/* Detection Rate */}
            <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('blueTeam.detectionRate')}</p>
                  <p className="text-3xl font-bold text-green-600">
                    {detectionRate?.detectedByResultRate || 0}%
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-xl" />
                </div>
              </div>
            </div>

            {/* Avg Time to Detect */}
            <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('blueTeam.avgTimeToDetect')}</p>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {detectionRate?.avgTimeToDetectFormatted || 'N/A'}
                  </p>
                </div>
                <div className="bg-yellow-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faClock} className="text-yellow-600 text-xl" />
                </div>
              </div>
            </div>

            {/* Correlation Rate */}
            <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('blueTeam.correlationRate')}</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {detectionRate?.correlationRate || 0}%
                  </p>
                </div>
                <div className="bg-purple-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faSearch} className="text-purple-600 text-xl" />
                </div>
              </div>
            </div>
          </div>

          {/* SIEM Import Review Stats */}
          {stats?.siemImported > 0 && (
            <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : ''}`}>
                <FontAwesomeIcon icon={faListCheck} className="mr-2 text-teal-500" />
                {t('blueTeam.siemReviewStats') || 'SIEM Import Review'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('siemReview.totalImported') || 'Imported'}</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.siemImported}</p>
                </div>
                <div>
                  <p className="text-sm text-teal-500">{t('siemReview.targetMatched') || 'Target Matched'}</p>
                  <p className="text-xl font-bold text-teal-600">{stats.targetMatched || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-yellow-500">{t('siemReview.pendingReview') || 'Pending'}</p>
                  <p className="text-xl font-bold text-yellow-600">{stats.siemPendingReview}</p>
                </div>
                <div>
                  <p className="text-sm text-green-500">{t('siemReview.confirmed') || 'Confirmed'}</p>
                  <p className="text-xl font-bold text-green-600">{stats.siemConfirmed}</p>
                </div>
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('siemReview.dismissed') || 'Dismissed'}</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{stats.siemDismissed}</p>
                </div>
              </div>
              <div className="mt-3">
                <Link
                  to={`/projects/${projectId}/blue-team/siem-review`}
                  className="text-teal-600 hover:text-teal-800 text-sm"
                >
                  {t('siemReview.reviewButton') || 'Go to SIEM Review'} &rarr;
                </Link>
              </div>
            </div>
          )}
        </>
      )}

      {/* ====== TAB: ANALYSIS ====== */}
      {activeTab === 'analysis' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Severity Breakdown */}
          <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : ''}`}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-orange-500" />
              {t('blueTeam.findingsBySeverity')}
            </h3>
            <div className="space-y-3">
              {['critical', 'high', 'medium', 'low', 'informational'].map(severity => (
                <div key={severity} className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(severity)}`}>
                    {severity.charAt(0).toUpperCase() + severity.slice(1)}
                  </span>
                  <div className="flex items-center">
                    <div className={`w-32 rounded-full h-2 mr-3 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                      <div
                        className={`h-2 rounded-full ${
                          severity === 'critical' ? 'bg-red-500' :
                          severity === 'high' ? 'bg-orange-500' :
                          severity === 'medium' ? 'bg-yellow-500' :
                          severity === 'low' ? 'bg-blue-500' : 'bg-gray-500'
                        }`}
                        style={{
                          width: `${stats?.totalFindings ? ((stats.bySeverity[severity] || 0) / stats.totalFindings * 100) : 0}%`
                        }}
                      />
                    </div>
                    <span className={`text-sm font-medium w-8 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {stats?.bySeverity?.[severity] || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detection Sources */}
          <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : ''}`}>
              <FontAwesomeIcon icon={faChartLine} className="mr-2 text-blue-500" />
              {t('blueTeam.detectionSources')}
            </h3>
            <div className="space-y-3">
              {Object.entries(stats?.bySource || {}).map(([source, count]) => (
                <div key={source} className="flex items-center justify-between">
                  <span className={`text-sm capitalize ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {source.replace(/_/g, ' ')}
                  </span>
                  <div className="flex items-center">
                    <div className={`w-32 rounded-full h-2 mr-3 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                      <div
                        className="h-2 rounded-full bg-blue-500"
                        style={{
                          width: `${stats?.totalFindings ? (count / stats.totalFindings * 100) : 0}%`
                        }}
                      />
                    </div>
                    <span className={`text-sm font-medium w-8 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{count}</span>
                  </div>
                </div>
              ))}
              {Object.keys(stats?.bySource || {}).length === 0 && (
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('blueTeam.noDetectionSources')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ====== TAB: FINDINGS ====== */}
      {activeTab === 'findings' && (
        <div className={`rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center">
              <h3 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : ''}`}>
                <FontAwesomeIcon icon={faBell} className="mr-2 text-blue-500" />
                {t('blueTeam.recentFindings')}
              </h3>
              <Link
                to={`/projects/${projectId}/blue-team/findings`}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                {t('common.viewAll')}
              </Link>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('blueTeam.time')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('common.title')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('blueTeam.severity')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('blueTeam.source')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('common.status')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('blueTeam.technique')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('blueTeam.correlated')}
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {findings.slice(0, 10).map(finding => (
                  <tr key={finding._id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {format(new Date(finding.detectionTimestamp), 'MMM d, HH:mm')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/projects/${projectId}/blue-team/findings/${finding._id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        {finding.title}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(finding.severity)}`}>
                        {finding.severity}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm capitalize ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {finding.detectionSource?.replace(/_/g, ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(finding.status)}`}>
                        {finding.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {finding.suspectedTechnique?.id || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {finding.correlationStatus === 'matched' ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                          {t('common.yes')}
                        </span>
                      ) : (
                        <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('common.no')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {findings.length === 0 && (
              <div className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                <p className="mb-4">{t('blueTeam.noFindings')}</p>
                {isAdmin && (
                  <button
                    onClick={handleSeedBlueTeam}
                    disabled={seedLoading}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  >
                    {seedLoading ? (
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    ) : (
                      <FontAwesomeIcon icon={faDatabase} className="mr-2" />
                    )}
                    {seedLoading ? 'Seeding...' : 'Seed Sample Data'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====== TAB: DETECTION (Admin/Purple only) ====== */}
      {activeTab === 'detection' && canSeeRedTeamData && (
        <>
          {/* Undetected Techniques Alert */}
          {detectionRate?.undetectedTechniques?.length > 0 && (
            <div className={`border rounded-lg p-6 ${darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
              <h3 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-red-400' : 'text-red-800'}`}>
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                {t('blueTeam.undetectedTechniques')}
                <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  Admin/Purple View
                </span>
              </h3>
              <p className={`text-sm mb-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                {t('blueTeam.undetectedDescription')}
              </p>
              <div className="flex flex-wrap gap-2">
                {detectionRate.undetectedTechniques.map(tech => (
                  <span
                    key={tech.id}
                    className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm"
                  >
                    {tech.id} - {tech.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Technique Detection Rates */}
          {detectionRate?.techniqueDetection?.length > 0 && (
            <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : ''}`}>
                {t('blueTeam.detectionRateByTechnique')}
                <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  Admin/Purple View
                </span>
              </h3>
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('blueTeam.technique')}</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('blueTeam.tactic')}</th>
                      <th className={`px-4 py-2 text-center text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('blueTeam.total')}</th>
                      <th className={`px-4 py-2 text-center text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('blueTeam.detected')}</th>
                      <th className={`px-4 py-2 text-center text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('blueTeam.rate')}</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {detectionRate.techniqueDetection.map(tech => (
                      <tr key={tech.id} className={tech.detected === 0 ? (darkMode ? 'bg-red-900/20' : 'bg-red-50') : ''}>
                        <td className="px-4 py-2">
                          <span className="font-medium text-blue-600">{tech.id}</span>
                          <span className={`text-sm ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tech.name}</span>
                        </td>
                        <td className={`px-4 py-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tech.tactic}</td>
                        <td className={`px-4 py-2 text-center ${darkMode ? 'text-gray-300' : ''}`}>{tech.total}</td>
                        <td className={`px-4 py-2 text-center ${darkMode ? 'text-gray-300' : ''}`}>{tech.detected}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded text-sm ${
                            tech.detectionRate >= 75 ? 'bg-green-100 text-green-800' :
                            tech.detectionRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {tech.detectionRate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(!detectionRate?.undetectedTechniques?.length && !detectionRate?.techniqueDetection?.length) && (
            <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <FontAwesomeIcon icon={faCrosshairs} className="text-4xl mb-3 opacity-30" />
              <p>{t('blueTeam.noDetectionData') || 'No detection data available yet'}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BlueTeamDashboard;
